import React, { createContext, useMemo, type ReactNode } from "react";

import { GET_LABEL_RULES_V3 } from "@gql/overview-v2";
import { useNorthbeamQuery } from "@hooks/http";
import { type GetLabelRulesV3 } from "@north-beam/nb-client-side";

import { CreateSavedViewModal } from "./components";
import { LEVEL_TO_NUMBER, NUMBER_TO_LEVEL } from "./components/tile/utils";
import { useSavedViews, type SavedViewsManager } from "./hooks";
import { type Level } from "./types";

export interface OverviewContextManager
  extends Omit<
    SavedViewsManager,
    | "isCustomGoalsLoading"
    | "dismissUnsavedAction"
    | "persistUnsavedAction"
    | "notifyUserOfTemplateChange"
  > {
  isMetadataLoading: boolean;
  labelsByLevel: Record<string, Level>;
}

export const OverviewContext = createContext<OverviewContextManager | null>(
  null,
);

export const OverviewProvider = ({ children }: { children: ReactNode }) => {
  const {
    saveViewsSavingState,
    notifyUserOfTemplateChange,
    dismissUnsavedAction,
    persistUnsavedAction,
    isCustomGoalsLoading,
    createHiddenSavedViewTemplate,

    ...manager
  } = useSavedViews();

  const { data: labelRulesData, loading: isLabelRulesLoading } =
    useNorthbeamQuery<GetLabelRulesV3>(GET_LABEL_RULES_V3, {});

  const labelsByLevel = useMemo(() => {
    if (!labelRulesData) {
      return {};
    }

    const labelsByLevelNumber: Record<string, number> = {};

    labelRulesData.me.labelRulesV3.forEach((breakdown) => {
      const levelNumber = LEVEL_TO_NUMBER[breakdown.level] ?? 0;

      breakdown.labelRules.forEach((label) => {
        const labelValue = label.output.match(/\{\{.*\}\}/)
          ? "*"
          : label.output;
        const key = `${breakdown.labelKey}:${labelValue}`;

        if (!labelsByLevelNumber[key]) {
          labelsByLevelNumber[key] = 0;
        }

        if (labelsByLevelNumber[key] < levelNumber) {
          labelsByLevelNumber[key] = levelNumber;
        }
      });

      const notSetKey = `${breakdown.labelKey}:`;

      if (!labelsByLevelNumber[notSetKey]) {
        labelsByLevelNumber[notSetKey] = 0;
      }

      if (labelsByLevelNumber[notSetKey] < levelNumber) {
        labelsByLevelNumber[notSetKey] = levelNumber;
      }
    });

    const result: Record<string, Level> = {};

    Object.entries(labelsByLevelNumber).forEach(([key, value]) => {
      result[key] = NUMBER_TO_LEVEL[value];
    });

    return result;
  }, [labelRulesData]);

  return (
    <OverviewContext.Provider
      value={{
        ...manager,
        labelsByLevel,
        saveViewsSavingState,
        createHiddenSavedViewTemplate,
        isMetadataLoading:
          saveViewsSavingState.isLoading ||
          isCustomGoalsLoading ||
          isLabelRulesLoading,
      }}
    >
      {children}
      <CreateSavedViewModal
        isOpen={notifyUserOfTemplateChange}
        description="Edits to Northbeam templates must be saved as new saved views."
        onClose={() => dismissUnsavedAction()}
        cancelText="No, Cancel"
        submitText="Yes, Save as New View"
        title="Save changes as new view?"
        onSubmit={(name) =>
          persistUnsavedAction(
            name,
            { container: "saved-views-modal", eventType: "submit" },
            { errorMessage: "There was an error creating the view." },
          )
        }
      />
    </OverviewContext.Provider>
  );
};
